// Phase 2D.1C — the Owner invitation token: FRAGMENT ONLY, MEMORY ONLY.
//
// WHY A FRAGMENT
//   A query string travels in the HTTP request line, so it reaches the access
//   log of every hop (ingress, reverse proxy, application), the browser history
//   entry that is synced across devices, and any `Referer` header. A fragment
//   never leaves the browser.
//
// WHAT THIS MODULE GUARANTEES
//   * the raw token is read SYNCHRONOUSLY at boot, from the very first module
//     the bundle executes, before React mounts, before the Supabase client is
//     created and before any API client, analytics or telemetry code exists;
//   * it is removed from the address bar immediately with history.replaceState,
//     so it is gone before the first network request can carry a Referer;
//   * it is kept in a module-scoped variable. It is NEVER written to
//     localStorage, sessionStorage, a cookie, the DOM, a log line or an error
//     report, and it is never appended to a URL by browser code;
//   * a Supabase Auth fragment (`access_token`, `refresh_token`, an `error`) is
//     left completely untouched: it belongs to the Supabase SDK, which consumes
//     it itself.
//
// The token alone NEVER authorizes anything. The backend re-hashes it and also
// requires a verified Supabase session whose e-mail matches the invitation.
//
// This module must stay dependency-free so nothing can run before it.

let rawToken = null;
let landedWithToken = false;
let landedWithAuthInvite = false;
let landedWithLinkError = false;
let landedWithExpiredLink = false;

function fragmentParams() {
  if (typeof window === "undefined" || !window.location) return null;
  const fragment = String(window.location.hash || "").replace(/^#/, "");
  if (!fragment) return null;
  return new URLSearchParams(fragment);
}

function stripFragment() {
  if (typeof window === "undefined" || !window.history || !window.history.replaceState) return;
  try {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  } catch {
    // A browser that refuses replaceState keeps the address bar as it is. The
    // token is still only in memory and is still never sent in a URL.
  }
}

export function captureInviteLink() {
  const params = fragmentParams();
  if (!params) return;

  // A Supabase Auth landing: the SDK owns this fragment.
  if (params.has("access_token") || params.has("refresh_token")) {
    landedWithAuthInvite = params.get("type") === "invite";
    return;
  }
  if (params.has("error") || params.has("error_code") || params.has("error_description")) {
    landedWithLinkError = true;
    landedWithExpiredLink = /expired/i.test(
      `${params.get("error_code") || ""} ${params.get("error") || ""}`,
    );
    return;
  }

  const value = params.get("token");
  if (!value) return;
  rawToken = value;
  landedWithToken = true;
  stripFragment();
}

captureInviteLink();

/** The raw token, or null. Memory only — never persisted, never logged. */
export function readInviteToken() {
  return rawToken;
}

/** Drop the token as soon as it has been used. */
export function clearInviteToken() {
  rawToken = null;
}

export function inviteTokenWasPresented() {
  return landedWithToken;
}

/** The person arrived through a genuine Supabase Admin invitation link. */
export function landedAsAuthInvite() {
  return landedWithAuthInvite;
}

export function inviteLinkFailed() {
  return landedWithLinkError;
}

export function inviteLinkExpired() {
  return landedWithExpiredLink;
}

/** True when no invitation token is left anywhere in the address bar. */
export function inviteTokenIsStrippedFromUrl() {
  if (typeof window === "undefined" || !window.location) return true;
  return !`${window.location.hash || ""}${window.location.search || ""}`.includes("token=");
}
